import { useTranslation } from 'react-i18next';
import './Categories.css';
import { Link } from 'react-router-dom';
import { fetchCategories } from "../../redux/actions/CategoriesAction";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useDarkMode } from "../../App";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            ease: "easeOut"
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: "easeOut"
        }
    }
};

function CategoriesPage() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12);

    const {
        categories,
        isLoading,
        error
    } = useSelector((state) => state.categories);

    useEffect(() => {
        window.scrollTo(0, 0);
        dispatch(fetchCategories(i18n.language));
    }, [dispatch, i18n.language]);

    const { currentCategories, totalPages } = useMemo(() => {
        const indexOfLastCategory = currentPage * itemsPerPage;
        const indexOfFirstCategory = indexOfLastCategory - itemsPerPage;
        const currentCategories = categories?.slice(indexOfFirstCategory, indexOfLastCategory) || [];
        const totalPages = Math.ceil((categories?.length || 0) / itemsPerPage);
        return { currentCategories, totalPages };
    }, [currentPage, itemsPerPage, categories]);

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    if (isLoading) return (
        <div className="modern-loading-container">
            <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="loading-content"
            >
                <div className="premium-loader"></div>
                <p className="mt-4 text-gradient">{t('common.loading', 'جاري تحميل الأقسام...')}</p>
            </motion.div>
        </div>
    );
    
    if (error) return (
        <div className="container py-5 mt-5">
            <motion.div 
                className="error-glass-card text-center p-5"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <i className="fas fa-exclamation-circle text-danger display-1 mb-4"></i>
                <h2 className="fw-bold mb-3">{t('common.error', 'حدث خطأ ما')}</h2>
                <p className="lead text-muted mb-4">{error}</p>
                <button 
                    onClick={() => window.location.reload()} 
                    className="btn btn-primary rounded-pill px-5 py-3 shadow-lg"
                >
                    {t('common.retry', 'إعادة المحاولة')}
                </button>
            </motion.div>
        </div>
    );

    return (
        <div className={`categories-page-wrapper ${isDarkMode ? 'dark-mode' : ''}`}>
            {/* Hero Section */}
            <motion.div 
                className="categories-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            >
                <div className="hero-overlay"></div>
                <div className="hero-bg-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                    <div className="shape shape-3"></div>
                </div>
                
                <div className="container h-100 d-flex flex-column justify-content-center align-items-center position-relative text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.8 }}
                    >
                        <h1 className="hero-title">
                            <span className="text-glow">{t('categories.title')}</span>
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb justify-content-center premium-breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to="/">{t('categories.home')}</Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {t('categories.title')}
                                </li>
                            </ol>
                        </nav>
                    </motion.div>
                </div>
            </motion.div>

            <section className="categories-grid-section py-5">
                <div className="container">
                    {/* Decorative Elements */}
                    <div className="floating-decorations">
                        <div className="orb orb-1"></div>
                        <div className="orb orb-2"></div>
                    </div>

                    <motion.div 
                        className="row g-5"
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {currentCategories?.length > 0 ? (
                            currentCategories.map((category, index) => (
                                <motion.div 
                                    className="col-xl-3 col-lg-4 col-md-6" 
                                    key={category.id}
                                    variants={cardVariants}
                                >
                                    <Link to={`/categories/${category.id}`} className="text-decoration-none h-100 d-block">
                                        <div className="mythic-category-card h-100">
                                            <div className="card-inner">
                                                <div className="card-image-wrapper">
                                                    {category.image ? (
                                                        <img 
                                                            src={category.image} 
                                                            alt={category.name} 
                                                            className="card-image"
                                                            loading="lazy"
                                                        />
                                                    ) : (
                                                        <div className="card-image-placeholder">
                                                            <i className="fas fa-layer-group"></i>
                                                        </div>
                                                    )}
                                                    <div className="image-overlay"></div>
                                                    <div className="card-badge">
                                                        <i className="fas fa-chevron-right"></i>
                                                    </div>
                                                </div>
                                                
                                                <div className="card-content">
                                                    <h3 className="category-name">{category.name}</h3>
                                                    <p className="category-desc">
                                                        {category.description || t('categories.defaultDescription', 'استكشف مجموعة واسعة من المنتجات في هذا القسم المميز.')}
                                                    </p>
                                                    <div className="card-footer-action">
                                                        <span className="view-more-text">
                                                            {t('categories.viewSubcategories', 'عرض الأقسام')}
                                                        </span>
                                                        <div className="animated-line"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))
                        ) : (
                            <motion.div 
                                className="col-12"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                <div className="empty-mythic-state">
                                    <div className="empty-icon-box">
                                        <i className="fas fa-search"></i>
                                    </div>
                                    <h3>{t('categories.noCategories', 'لا توجد أقسام حالياً')}</h3>
                                    <p>{t('categories.tryAgainLater', 'سنقوم بإضافة المزيد من الأقسام قريباً، ابقَ على اطلاع.')}</p>
                                </div>
                            </motion.div>
                        )}
                    </motion.div>

                    {/* Enhanced Pagination */}
                    {totalPages > 1 && (
                        <motion.div 
                            className="mythic-pagination mt-5 pt-5"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="pagination-wrapper">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`page-nav-btn prev ${currentPage === 1 ? 'disabled' : ''}`}
                                >
                                    <i className="fas fa-arrow-right"></i>
                                </button>

                                <div className="page-numbers">
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        if (
                                            pageNumber === 1 ||
                                            pageNumber === totalPages ||
                                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                        ) {
                                            return (
                                                <button
                                                    key={index}
                                                    className={`page-num-btn ${pageNumber === currentPage ? 'active' : ''}`}
                                                    onClick={() => handlePageChange(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </button>
                                            );
                                        } else if (
                                            (pageNumber === currentPage - 2 && currentPage > 3) ||
                                            (pageNumber === currentPage + 2 && currentPage < totalPages - 2)
                                        ) {
                                            return <span key={index} className="page-dots">...</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`page-nav-btn next ${currentPage === totalPages ? 'disabled' : ''}`}
                                >
                                    <i className="fas fa-arrow-left"></i>
                                </button>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default CategoriesPage;